import * as React from 'react';
import Link from 'next/link';
import { cn } from '@/util/cn';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';

// SEO intro block that sits at the top of most region / resort pages
// (Snow Report · Weather · Webcams · etc.). Small bold secondary-blue
// title (sm on mobile, lg from md up) + a 2-3 sentence muted paragraph.
// Optional right-aligned link in the title row for surfaces that need a
// CTA (e.g. "Live Webcams »").
//
// Both size classes are load-bearing: <Heading level={2}> defaults to
// `text-xl md:text-2xl`, and tailwind-merge only resolves conflicts
// within the same breakpoint — so `md:text-lg` is what keeps the md+
// size from falling back to the level-2 default.
//
// Carries NO outer margin. The consuming page owns vertical rhythm —
// typically by wrapping its major blocks in a `space-y-*` container.
// Pass `className` if a specific surface needs to add margin or padding.

export type SeoSectionProps = {
  title: string;
  /** Body content. Plain string, or React nodes for inline emphasis. */
  children: React.ReactNode;
  /** Optional right-aligned link in the title row. */
  link?: { href: string; label: string };
  className?: string;
};

export const SeoSection = ({
  title, children, link, className,
}: SeoSectionProps) => (
  <section className={cn(className)}>
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <Heading level={2} className="font-heading text-sm font-semibold text-secondary md:text-lg">
        {title}
      </Heading>
      {link && (
        <Link href={link.href} className="shrink-0 text-sm font-semibold text-primary hover:underline">
          {link.label}
        </Link>
      )}
    </div>
    <Text variant="small" className="leading-relaxed text-muted-foreground">
      {children}
    </Text>
  </section>
);
